// src/components/Sections/ProjectsPreview.jsx
import React, { useMemo } from "react";
import { motion } from "framer-motion";
import { FaYoutube, FaInstagram, FaLinkedin, FaArtstation } from "react-icons/fa";
import SEO from "../SEO"; // SEO component
import "./ProjectsPreview.css";

export default function ProjectsPreview() {
  // Memoized social list → prevents unnecessary rerenders
  const socials = useMemo(
    () => [
      {
        name: "YouTube Channel 1",
        link: "https://www.youtube.com/@Ankielabs",
        icon: <FaYoutube color="#FF0000" size={40} />,
        description: "Official Ankielabs YouTube Channel — 3D production, VFX & tutorials",
        image: "/images/seo-youtube1.webp",
      },
      {
        name: "YouTube Channel 2",
        link: "https://www.youtube.com/@Ankielabskids",
        icon: <FaYoutube color="#FF0000" size={40} />,
        description: "Ankielabs Kids — 3D animations & educational content for children",
        image: "/images/seo-youtube2.webp",
      },
      {
        name: "Artstation",
        link: "https://www.artstation.com/ankielabs",
        icon: <FaArtstation color="#4b7fd2" size={40} />,
        description: "Portfolio on Artstation — 3D modeling, texturing, and lighting",
        image: "/images/seo-artstation.webp",
      },
      {
        name: "Instagram",
        link: "https://www.instagram.com/ankielabs/",
        icon: <FaInstagram color="#E1306C" size={40} />,
        description: "Follow Ankielabs on Instagram for creative 3D visuals and stories",
        image: "/images/seo-instagram.webp",
      },
      {
        name: "LinkedIn",
        link: "https://www.linkedin.com/company/ankielabs/",
        icon: <FaLinkedin color="#0A66C2" size={40} />,
        description: "Ankielabs LinkedIn profile — professional updates & networking",
        image: "/images/seo-linkedin.webp",
      },
    ],
    []
  );

 // Use first social link as preview with absolute URL check and window safety fallback
  const firstSocial = useMemo(() => socials[0], [socials]);
  const socialImageUrl = firstSocial?.image?.startsWith("http") 
    ? firstSocial.image 
    : `https://ankielabs.com${firstSocial?.image || "/images/anikielab.webp"}`;
    
  const currentUrl = typeof window !== "undefined" ? window.location.href : "https://ankielabs.com";

  return (
    <>
      {/* 🔥 Dynamic SEO: Use first social link as preview */}
      <SEO
        title="Connect with Ankielabs — 3D Production, VFX & Creative Studio"
        description="Follow Ankielabs on YouTube, Instagram, LinkedIn, and Artstation for creative 3D content, VFX, and tutorials."
        image={socialImageUrl}
        url={currentUrl}
        schemaType="Organization"
        schemaData={{
          sameAs: socials.map((social) => social.link).filter(Boolean)
        }}
      />
      
      <motion.section
        className="section"
        initial={{ opacity: 0, y: 50 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, amount: 0.2 }}
        transition={{ duration: 0.6, ease: "easeOut" }}
        style={{ willChange: "transform" }}
      >
        <motion.h2
          initial={{ opacity: 0, y: -20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="social-title"
          viewport={{ once: true }}
        >
          Connect with Us
        </motion.h2>

        <div className="preview-grid">
          {socials.map((item, i) => (
            <motion.a
              key={item.name}
              href={item.link}
              target="_blank"
              rel="noopener noreferrer"
              className="social-card"
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.12, duration: 0.45 }}
              whileHover={{
                scale: 1.08,
                boxShadow: "0 0 25px rgba(255,255,255,0.3)",
                transition: { duration: 0.25 },
              }}
              whileTap={{ scale: 0.95 }}
              style={{ willChange: "transform" }}
            >
              <div className="icon-container">{item.icon}</div>
              <p>{item.name}</p>
            </motion.a>
          ))}
        </div>
      </motion.section>
    </>
  );
}
